import React from "react";
import { SitesResponse, SlateResponse, SportsResponse } from "../store/types";
import { setSite, setSlate, setSport, setType } from "../store/actions";
import { connect, useDispatch } from "react-redux";
import { Box, Stack } from "@mui/material";
import { sportLogos, siteLogos } from "../../utils/logos";
import { SelectInput, MenuOption } from "../selectOptions";
import { useGetSlates } from "../queries/slateQueries";
import { useHistory } from "react-router-dom";
import { MoonLoader } from "react-spinners";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { setLineupVerified } from "../../preContest/store/actions";
import { filterSlates, getSupportedSports, SupportedSites } from "../helpers/SelectorHelpers";
import { setSelectedLineup } from "../../LineupBuilder/store/actions";

interface OwnProps {
  readonly app?: string;
}

interface ReduxProps {
  readonly selectedSport: SportsResponse;
  readonly selectedSite: SitesResponse;
  readonly selectedType: string;
  readonly selectedSlate: SlateResponse;
}

interface DispatchProps {
  readonly SetSelectedSport: (newValue: SportsResponse) => void;
  readonly SetSelectedSite: (newValue: SitesResponse) => void;
  readonly SetSelectedType: (newValue: string) => void;
  readonly SetSelectedSlate: (newValue: SlateResponse) => void;
  readonly SetLineupVerified: (verified: boolean) => void;
}

type Props = OwnProps & ReduxProps & DispatchProps;

const SettingsSelectors = (props: Props) => {
  const {
    selectedSite,
    selectedSlate,
    selectedType,
    selectedSport,
    app,
    SetSelectedSport,
    SetSelectedSite,
    SetSelectedSlate,
    SetSelectedType,
    SetLineupVerified,
  } = props;
  const [search, setSearch] = useQueryParams({ site: StringParam, slate: NumberParam});
  const history = useHistory();
  const {data, isLoading: slatesLoading} = useGetSlates(app || 'CONTESTSIM', false);
  const slates = data?.slates;
  const sports = getSupportedSports(app || 'CONTESTSIM');
  const sites = SupportedSites;
  const dispatch = useDispatch();

  const handleSportSelect = (sport: SportsResponse) => {
    SetSelectedSport(sport);
    SetLineupVerified(false);
    dispatch(setSelectedLineup(null));
    const newSlate = filterSlates(slates || [], sport, selectedSite, selectedType)[0];
    if (newSlate) {
      SetSelectedSlate(newSlate);
      setSearch({ ...search, slate: parseInt(newSlate.slateId)}, 'push');
    }
    else {
      SetSelectedSlate({} as SlateResponse);
      setSearch({ ...search, slate: undefined}, 'push');
    }
    history.push(`${sport.sport}`);
  }

  const handleSiteSelect = (site: SitesResponse) => {
    SetSelectedSite(site);
    dispatch(setSelectedLineup(null));
    const newSlate = filterSlates(slates || [], selectedSport, site, selectedType)[0];
    
    if (newSlate) {
      SetSelectedSlate(newSlate);
      setSearch({ ...search, slate: parseInt(newSlate.slateId), site: site.site}, 'push');
    } 
    else {
      SetSelectedSlate({} as SlateResponse);
      setSearch({ ...search, slate: undefined, site: site.site}, 'push');
    }
    SetLineupVerified(false);
  };

  const handleTypeSelect = (type: string) => {
    SetSelectedType(type);
    dispatch(setSelectedLineup(null));
    const newSlate = filterSlates(slates || [], selectedSport, selectedSite, type)[0];
    if (newSlate) {
      SetSelectedSlate(newSlate);
      setSearch({ ...search, slate: parseInt(newSlate.slateId)}, 'push');
    }
    else {
      SetSelectedSlate({} as SlateResponse);
      setSearch({ ...search, slate: undefined}, 'push');
    }
    SetLineupVerified(false);
  }

  const handleSlateSelect = (slate: SlateResponse) => {
    SetSelectedSlate(slate);
    dispatch(setSelectedLineup(null));
    setSearch({ ...search, slate: parseInt(slate.slateId)}, 'push');
  }

  React.useEffect(() => {
    // change to showdown for MMA + FD
    if ( selectedSport?.sport === 'MMA' && selectedSite.site === 'FD') {
      handleTypeSelect('SHOWDOWN')
    }
  },[selectedSport, selectedSite])

  React.useEffect(() => {
    if(selectedSlate.slateId?.toString() !== search.slate?.toString() && search.slate) {
      const newSlate = slates?.find(x => x.slateId.toString() === search.slate?.toString());
      if (newSlate) {
        SetSelectedSlate(newSlate);
        SetSelectedType(newSlate.type);
      }
    }
  }, [search.slate, selectedSlate, slates])

  React.useEffect(() => {
    if(selectedSite.site !== search.site && search.site) {
      const newSite = sites?.find(x => x.site.toUpperCase() === search.site?.toUpperCase());
      if (newSite) {
        SetSelectedSite(newSite);
      }
    }
  }, [search.site, selectedSite, sites])

  if (slatesLoading) { 
    return (
      <MoonLoader color="white" size={30} />
    )
  }


  return (
    <Stack direction="row" justifyContent={{ xs: 'unset', lg: 'flex-start'}} flexWrap='wrap'>
      <Box>
        <SelectInput
          data={sports?.map(x => ({ name: x.sport, logo: sportLogos[x.sport?.toUpperCase()]?.logo, invertBackground: sportLogos[x.sport?.toUpperCase()]?.invertBackground, value: x.sport, option: x, } as MenuOption<SportsResponse>)) ?? []}
          label="League"
          value={selectedSport?.sport || 'NFL'}
          onSelect={(selection: any) => handleSportSelect(selection as SportsResponse)}
        />
      </Box>
      <Box>
        <SelectInput
          data={sites?.map(x => ({ name: siteLogos[x.site?.toLowerCase()]?.name || x.site, logo: siteLogos[x.site?.toLowerCase()]?.logo || '' , value: x.site, option: x, } as MenuOption<SitesResponse>))}
          label="Site"
          value={selectedSite?.site || 'DK'}
          onSelect={(selection: any) => handleSiteSelect(selection as SitesResponse)}
        />
      </Box>
      <Box>
        <SelectInput
          data={[{ name: 'Classic', value: 'CLASSIC', option: 'CLASSIC' }, { name: 'Showdown', value: 'SHOWDOWN', option: 'SHOWDOWN' }]}
          label="Type"
          value={selectedType || 'CLASSIC'}
          onSelect={(selection: string) => handleTypeSelect(selection)}
          />
      </Box>
      {
        slates && filterSlates(slates, selectedSport, selectedSite, selectedType)?.length > 0 && (
          <Box>
            <SelectInput
              data={filterSlates(slates, selectedSport, selectedSite, selectedType)?.map(x => ({ name: `${x.name} (${new Date(x.startTime).toLocaleDateString([], { month: 'short', day: '2-digit', timeStyle: undefined })} - ${new Date(x.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} EST)`, value: x.slateId, option: x } as MenuOption<SlateResponse>))}
              label="Slate"
              bottomLabel={selectedSport?.sport?.toUpperCase() === 'MLB' ? 'All day slates are not currently supported.' : ''}
              value={selectedSlate?.slateId}
              onSelect={(selection: any) => handleSlateSelect(selection as SlateResponse)}
              />
          </Box>
        )
      }
    </Stack>
  );
};


const mapStateToProps = (state: any) => {
  const {
    selectedSite,
    selectedSlate,
    selectedType,
    selectedSport,
  } = state.common;

  return {
    selectedSite,
    selectedSlate,
    selectedType,
    selectedSport,
  };
};

const mapDispatchToProps = {

  SetSelectedSport: setSport,
  SetSelectedSite: setSite,
  SetSelectedSlate: setSlate,
  SetSelectedType: setType,
  SetLineupVerified: setLineupVerified
};

export default connect(mapStateToProps, mapDispatchToProps)(SettingsSelectors);



